import { motion } from 'framer-motion';
import { useLang } from '../i18n/LanguageContext';
import { FadeUp, Stagger, staggerItem, LineReveal } from '../components/Reveal';
import Marquee from '../components/Marquee';
import ChannelLogo from '../components/ChannelLogo';

export default function Integrations() {
  const { t } = useLang();

  // Five integration cards: WhatsApp, Instagram, Microsoft Teams, Slack, Web Widget.
  const channels = [
    { name: 'WhatsApp', key: 'channels.whatsapp', type: 'whatsapp', brand: 'linear-gradient(90deg,#25D366,#00A884)' },
    { name: 'Instagram', key: 'channels.instagram', type: 'instagram', brand: 'linear-gradient(90deg,#F58529,#DD2A7B,#8134AF)' },
    { name: 'Microsoft Teams', key: 'channels.teams', type: 'teams', brand: 'linear-gradient(90deg,#6264A7,#8B5CF6,#4F46E5)' },
    { name: 'Slack', key: 'channels.slack', type: 'slack', brand: 'linear-gradient(90deg,#36C5F0,#611F69,#E01E5A)' },
    { name: 'Web Widget', key: 'channels.web', type: 'web', brand: 'linear-gradient(90deg,#00AEEF,#00C5C2)' },
  ];

  const proof = {
    whatsapp: ['Gyeno · Online', 'Can you check my deal status?', 'Sure — Acme is in Proposal and 80% toward close.'],
    instagram: ['Gyeno · Active now', 'Do you have a demo for enterprise plans?', 'Yes. I can schedule one for you.'],
    teams: ['Gyeno · General', 'Here’s the latest update on your opportunities.', 'Acme Corp · $120k · Proposal'],
    slack: ['# sales · Gyeno APP', 'New lead qualified! 🎉', 'Sarah Chen · CTO · Score 92/100'],
    web: ['Chat with Gyeno', 'Can you help me find the right plan?', 'Of course. Tell me your team size.'],
  };

  return (
    <div>
      <section className="bg-[#10282C] grain relative overflow-hidden pt-36 pb-16">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00C5C2]">
            {t('channels.kicker')}
          </p>
          <h1 className="mt-5 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-3xl">
            <LineReveal lines={[t('channels.title')]} />
          </h1>
        </div>
        <div className="relative z-10 mt-10">
          <Marquee dark items={['WhatsApp', 'Instagram', 'Microsoft Teams', 'Slack', 'Web Widget']} />
        </div>
      </section>

      <section className="py-24 bg-[#F6F8F7]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {channels.map(({ name, key, type, brand }) => {
              const [top, first, second] = proof[type];

              return (
                <motion.div
                  key={name}
                  variants={staggerItem}
                  data-testid={`integration-card-${name.toLowerCase().replace(/\s/g, '-')}`}
                  className="group overflow-hidden rounded-2xl border bg-white border-[#10282C]/8 hover:-translate-y-1"
                  style={{ transition: 'transform .3s cubic-bezier(.22,1,.36,1), box-shadow .3s ease' }}
                >
                  <div className="relative h-52 overflow-hidden bg-[#10282C]">
                    <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_50%_0%,rgba(0,197,194,.45),transparent_60%)]" />
                    <div className="absolute left-5 right-5 top-5 bottom-4 overflow-hidden rounded-xl border border-white/10 bg-[#0A2024] shadow-[0_18px_40px_-20px_rgba(0,0,0,.7)]">
                      <div className="flex items-center gap-2 border-b border-white/8 px-3 py-2.5">
                        <span className="h-2 w-2 rounded-full bg-[#FD584F]" />
                        <span className="h-2 w-2 rounded-full bg-[#F5C451]" />
                        <span className="h-2 w-2 rounded-full bg-[#5BD37A]" />
                        <span className="ml-2 truncate text-[10px] font-mono text-white/45">{top}</span>
                      </div>
                      <div className="space-y-2.5 p-3">
                        <div className="max-w-[84%] rounded-xl bg-white/[0.06] px-3 py-2 text-[10px] leading-relaxed text-white/70">{first}</div>
                        <div className="ml-auto max-w-[84%] rounded-xl bg-white/10 px-3 py-2 text-[10px] leading-relaxed text-white/85">{second}</div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 text-[#10282C]">
                    <div className="flex items-center gap-2.5">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white border border-[#10282C]/8 overflow-hidden">
                        <ChannelLogo type={type} />
                      </span>

                      <h2
                        className="font-display text-lg font-semibold bg-clip-text text-transparent"
                        style={{ backgroundImage: brand }}
                      >
                        {name}
                      </h2>

                      <span className="ml-auto px-2 py-0.5 rounded-full text-[8px] font-mono font-bold tracking-wider bg-[#00918F]/10 text-[#00918F]">
                        LIVE
                      </span>
                    </div>

                    <p className="mt-3 text-sm leading-relaxed text-[#10282C]/60">
                      {type === 'web' ? 'Chat with customers directly on your website.' : t(key)}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </Stagger>

          <FadeUp className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ['ONE AGENT', 'Same agent logic across every customer channel.'],
              ['REAL ACTIONS', 'Surface Salesforce context and take actions.'],
              ['GOVERNED', "Every action logged and visible inside your org's permission model."],
            ].map(([label, copy]) => (
              <div key={label} className="rounded-2xl border border-[#10282C]/8 bg-white p-6">
                <p className="text-[10px] font-mono font-bold tracking-[0.18em] text-[#00918F]">{label}</p>
                <p className="mt-2.5 text-sm leading-relaxed text-[#10282C]/65">{copy}</p>
              </div>
            ))}
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
